/**
 * Aircall Everywhere SDK provider — embeds the live Aircall Workspace
 * iframe inside the portal so reps can dial, answer, and SMS without
 * leaving the page.
 *
 * Architecture
 * ────────────
 * • ONE workspace iframe lives for the lifetime of the app. It is mounted
 *   into a fixed-position host div appended to `<body>` once on first
 *   mount, and is NEVER reparented after that. Browsers reload iframes
 *   on `appendChild()` to a new parent, which kills the SDK's session
 *   and event listeners. Instead we toggle `display: none` / position
 *   the host via CSS to mimic an expand/collapse dock.
 * • Components anywhere in the app consume `useAircall()` to:
 *     - call `dial(phoneNumber)` from the timeline composer
 *     - check `loggedIn` to gate UI affordances (best-effort, see below)
 *     - call `expand()` / `collapse()` to toggle the dock
 * • Incoming calls fire an `incoming_call` event → we look up the
 *   matching Collections case by phone number and screen-pop their
 *   Customer File.
 * • `onLogin` only fires on an active sign-in gesture — cookie-resumed
 *   sessions never trigger it. We poll `workspace.isLoggedIn()` every
 *   2s for 60s, then every 15s, to catch already-authenticated mounts.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import AircallWorkspace from "aircall-everywhere";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import apiClient from "@/lib/api";

const AircallContext = createContext(null);
export const useAircall = () => useContext(AircallContext);

const DOCK_CONTAINER_ID = "aircall-workspace-host";
const DOCK_WIDTH  = 376;
const DOCK_HEIGHT = 666;   // Aircall "big" workspace recommended size — call controls live in the bottom strip
const MARGIN      = 16;

export function AircallProvider({ children }) {
  const workspaceRef = useRef(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [ready, setReady] = useState(false);
  const [lastCall, setLastCall] = useState(null);
  // Paul's Jul 24 2026 request: portal iframe rings alongside desktop app +
  // native Aircall app so a single call rings 3 times. Toggling `muted`
  // hides the iframe entirely (hidden iframes have their WebRTC audio
  // paused by every modern browser), suppresses the screen-pop + auto-
  // expand behaviours, and shows a muted indicator in the dock chrome +
  // sidebar. Persisted in localStorage so the choice survives reloads.
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem("sf_aircall_muted") === "1"; }
    catch { return false; }
  });
  const mutedRef = useRef(muted);   // for use inside SDK callbacks below
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem("sf_aircall_muted", next ? "1" : "0"); } catch (_e) { /* localStorage blocked */ }
      if (next) {
        toast(
          "🔕 Aircall muted in this browser — calls will only ring in your desktop/mobile app.",
          { duration: 6000 },
        );
      } else {
        toast.success("🔔 Aircall unmuted — calls will now ring in this tab too.");
      }
      return next;
    });
  }, []);
  // Bumped whenever the SDK fires a call-related event. Timeline + other
  // consumers subscribe to this to know when to re-fetch their data.
  const [refreshTick, setRefreshTick] = useState({ at: 0, kind: null });
  // Dock position — right/bottom anchored. User can drag the chrome to
  // move both the chrome and the host (they share these coords).
  const [pos, setPos] = useState({ right: MARGIN, bottom: MARGIN });
  const navigate = useNavigate();

  // ── Screen-pop: resolve incoming caller to a Collections case ────────
  const screenPopFromPhone = useCallback(async (rawPhone, direction = "incoming") => {
    if (!rawPhone) return;
    try {
      const resp = await apiClient.get(
        `/collections/ageing/cases/by-phone/${encodeURIComponent(rawPhone)}`,
      );
      const match = resp.data?.match;
      if (match?.case_id) {
        const verb = direction === "incoming" ? "Inbound" : "Outbound";
        toast.success(`📞 ${verb}: ${match.customer_name} (${match.loan_application_id || "—"})`, {
          description: match.contact_label ? `via ${match.contact_label}` : undefined,
          duration: 8000,
        });
        setLastCall({ direction, phone: rawPhone, caseId: match.case_id, at: Date.now() });
        if (direction === "incoming") navigate(`/collections/ageing/${match.case_id}`);
      } else if (direction === "incoming") {
        toast(`📞 Incoming call from ${rawPhone} — no matching case`, { duration: 6000 });
      }
    } catch (e) {
      console.warn("Aircall screen-pop lookup failed:", e?.message);
    }
  }, [navigate]);

  // ── Initialise the SDK once. The host stays in body forever. ─────────
  useEffect(() => {
    if (workspaceRef.current) return;

    let host = document.getElementById(DOCK_CONTAINER_ID);
    if (!host) {
      host = document.createElement("div");
      host.id = DOCK_CONTAINER_ID;
      Object.assign(host.style, {
        position:        "fixed",
        right:           `${MARGIN}px`,
        bottom:          `${MARGIN}px`,
        width:           `${DOCK_WIDTH}px`,
        height:          `${DOCK_HEIGHT}px`,
        zIndex:          "9997",
        display:         "none",                 // Hidden until expanded
        backgroundColor: "#f8fafc",
        borderRadius:    "0 0 12px 12px",
        overflow:        "hidden",
        boxShadow:       "0 25px 50px -12px rgba(0,0,0,0.25)",
        border:          "1px solid rgba(0,0,0,0.06)",
        borderTop:       "0",
      });
      document.body.appendChild(host);
    }

    try {
      workspaceRef.current = new AircallWorkspace({
        domToLoadWorkspace: `#${DOCK_CONTAINER_ID}`,
        size:               "big",
        integrationToLoad:  "generic",
        onLogin: (settings) => {
          setLoggedIn(true);
          console.info("[Aircall] Logged in", settings?.user?.email || "");
        },
        onLogout: () => {
          setLoggedIn(false);
          console.info("[Aircall] Logged out");
        },
      });

      const ws = workspaceRef.current;
      ws.on("incoming_call", (call) => {
        // Respect the mute toggle — Paul's request. If the user has muted
        // the portal iframe, don't screen-pop, don't auto-expand and
        // don't toast (the iframe is display:none so audio is paused by
        // the browser). We still fire the refreshTick so timeline
        // consumers know a call event happened.
        if (!mutedRef.current) {
          screenPopFromPhone(call?.from, "incoming");
          setExpanded(true);
        }
        setRefreshTick({ at: Date.now(), kind: "incoming_call", call });
      });
      ws.on("outgoing_call", (call) => {
        if (!mutedRef.current) {
          screenPopFromPhone(call?.to, "outgoing");
        }
        setRefreshTick({ at: Date.now(), kind: "outgoing_call", call });
      });
      ws.on("outgoing_answered", (call) => {
        setRefreshTick({ at: Date.now(), kind: "outgoing_answered", call });
      });
      ws.on("call_ended", (call) => {
        // The webhook may need a beat to land before our /timeline aggregation
        // surfaces the new call entry — emit the tick on a small delay so
        // consumers refresh AFTER the backend has processed Aircall's
        // call.created / call.ended webhooks.
        setTimeout(() => {
          setRefreshTick({ at: Date.now(), kind: "call_ended", call });
        }, 2500);
      });
      ws.on("comment_saved", (call) => {
        setRefreshTick({ at: Date.now(), kind: "comment_saved", call });
      });

      setReady(true);

      // Poller — catch cookie-resumed sessions that don't fire onLogin
      let probeCount = 0;
      const fast = setInterval(() => {
        workspaceRef.current?.isLoggedIn?.((status) => {
          if (status) {
            setLoggedIn((prev) => {
              if (!prev) console.info("[Aircall] Detected pre-existing session via isLoggedIn probe");
              return true;
            });
            clearInterval(fast);
          }
        });
        probeCount += 1;
        if (probeCount > 30) {
          clearInterval(fast);
          setInterval(() => {
            workspaceRef.current?.isLoggedIn?.((s) => setLoggedIn(!!s));
          }, 15000);
        }
      }, 2000);
    } catch (e) {
      console.error("[Aircall] SDK init failed:", e);
    }
  }, [screenPopFromPhone]);

  // ── Sync host CSS with expanded + pos + muted state. Pure style updates
  // only — never reparent or detach the host so the iframe is never
  // reloaded. When muted, force display:none so hidden-iframe audio
  // suppression kicks in (all evergreen browsers pause WebRTC audio in
  // display:none iframes).
  useEffect(() => {
    const host = document.getElementById(DOCK_CONTAINER_ID);
    if (!host) return;
    host.style.display = (expanded && !muted) ? "block" : "none";
    host.style.right   = `${pos.right}px`;
    host.style.bottom  = `${pos.bottom}px`;
  }, [expanded, pos, muted]);

  // ── Public methods ───────────────────────────────────────────────────
  const dial = useCallback((rawPhone, opts = {}) => {
    if (!workspaceRef.current) {
      toast.error("Aircall workspace isn't ready yet.");
      return;
    }
    const phone = (rawPhone || "").trim();
    if (!phone) {
      toast.error("No phone number to dial.");
      return;
    }
    setExpanded(true);
    workspaceRef.current.send("dial_number", { phone_number: phone }, (success, data) => {
      if (success) {
        opts.onSuccess?.(data);
        return;
      }
      let reason = "";
      if (typeof data === "string") reason = data;
      else if (data && typeof data === "object") reason = data.message || data.code || data.error || JSON.stringify(data);
      else reason = String(data ?? "unknown error");
      const lower = reason.toLowerCase();
      if (lower.includes("in_call")) {
        toast.error("You're already on a call. Hang up before dialling another number.");
      } else if (lower.includes("not_logged") || lower.includes("not logged")) {
        toast.error("Sign into Aircall inside the dock first, then click Dial again.", { duration: 7000 });
        setExpanded(true);
      } else if (lower.includes("no answer from the workspace")) {
        // SDK couldn't postMessage the iframe — usually because the
        // workspace is still booting or the user just signed in and
        // the iframe is mid-redirect. Suggest a retry rather than
        // making the rep think dialling is broken.
        toast.error("Aircall is still loading — wait a couple of seconds and click Dial again.", { duration: 6000 });
        setExpanded(true);
      } else {
        toast.error(`Aircall dial failed: ${reason}`);
      }
      console.warn("[Aircall] dial failed:", data);
      opts.onError?.(data);
    });
  }, []);

  const expand   = useCallback(() => setExpanded(true),  []);
  const collapse = useCallback(() => setExpanded(false), []);

  const value = {
    workspace: workspaceRef.current,
    loggedIn,
    expanded,
    ready,
    lastCall,
    refreshTick,
    pos,
    setPos,
    containerId: DOCK_CONTAINER_ID,
    dockWidth:   DOCK_WIDTH,
    dockHeight:  DOCK_HEIGHT,
    dial,
    expand,
    collapse,
    setExpanded,
    muted,
    toggleMute,
  };

  return <AircallContext.Provider value={value}>{children}</AircallContext.Provider>;
}
